import type { invoiceInfo, SearchInvoiceInfo } from "../../../redux/slice/invoice/InvoiceSlice";
import { COMMISSION_TYPE_V3_DISPLAY_FALLBACK } from "../../../constants/applicationConstants";
import { getInvoiceList } from "../../../service/InvoiceService";
import {
  ExcelSheetColumn,
  ExcelSheetRow,
  extractInvoiceListPage,
  formatExcelCurrency,
  formatExcelDate,
  formatExcelPercent,
  parseExcelNumber,
} from "../../../utils/excelSheet";

export const FINANCE_HQ_SALES_SHEET_TITLE = "Finance HQ List - Sales Sheet";

export const FINANCE_HQ_SALES_SHEET_EXPORT_FILE = "Finance_HQ_List_Sales_Sheet.csv";

/** Statuses loaded by Finance HQ list search and Excel View. */
export const FINANCE_HQ_EXCEL_INVOICE_STATUSES = ["PAID", "PARTIAL", "UNPAID"];

/** Edited from Configuration → Config List (appconfig.config_name). */
export const FINANCE_HQ_EXCEL_INITIAL_LIMIT_CONFIG = "finance.hq.excel.initial.limit";
export const FINANCE_HQ_EXCEL_LOAD_MORE_LIMIT_CONFIG = "finance.hq.excel.load.more.limit";
/** Used only when the matching app config is missing or not a positive number. */
const FINANCE_HQ_EXCEL_INITIAL_LIMIT_FALLBACK = 250;
const FINANCE_HQ_EXCEL_LOAD_MORE_LIMIT_FALLBACK = 250;
export const FINANCE_HQ_EXCEL_MAX_EXPORT_RECORDS = 1000;

type FinanceHqAppConfigEntry = {
  configName?: string;
  configValue?: string | null;
};

export const readFinanceHqExcelLimit = (
  appConfigList: FinanceHqAppConfigEntry[] | null | undefined,
  configName: string,
  fallback: number
): number => {
  const raw = appConfigList?.find((item) => item?.configName === configName)?.configValue;
  const parsed = Number(typeof raw === "string" ? raw.trim() : raw);
  if (!Number.isFinite(parsed) || parsed < 1) return fallback;
  return Math.floor(parsed);
};

export const resolveFinanceHqExcelPageLimits = (
  appConfigList?: FinanceHqAppConfigEntry[] | null
): { initialLimit: number; loadMoreLimit: number } => ({
  initialLimit: readFinanceHqExcelLimit(
    appConfigList,
    FINANCE_HQ_EXCEL_INITIAL_LIMIT_CONFIG,
    FINANCE_HQ_EXCEL_INITIAL_LIMIT_FALLBACK
  ),
  loadMoreLimit: readFinanceHqExcelLimit(
    appConfigList,
    FINANCE_HQ_EXCEL_LOAD_MORE_LIMIT_CONFIG,
    FINANCE_HQ_EXCEL_LOAD_MORE_LIMIT_FALLBACK
  ),
});
/** Brief spinner on reopen so cached invoices do not flash in instantly. */
export const FINANCE_HQ_EXCEL_REOPEN_LOADING_MS = 600;

export const FINANCE_HQ_SALES_SHEET_PAYMENT_SOURCE = "payments";
export const FINANCE_HQ_SALES_SHEET_OVERRIDE_SOURCE = "overrides";

/**
 * Column map for the Finance HQ sales sheet.
 * Add a new field here (id + label + type/editable/total) to grow the grid
 * without changing ExcelSheetPopup — keep `id` aligned with the mapper below.
 * Payment columns use `subRowsFrom` so multiple payments split the cell.
 * Override columns also set `nestedSubRowsFrom` so extra override agencies
 * stack under the same payment without splitting payment/agency cells.
 * `group` merges consecutive columns under one header (Order/Invoice/Payment/
 * Primary Org/Agency/Override commission).
 * V3 entity column *labels* here are defaults only; Finance HQ UI uses
 * applyV3EntityLabelsToFinanceHqColumns() with CommissionType displayValue (key V3).
 * Column ids (e.g. totalV3Cost, v3NetComm) must stay aligned with API row fields — not display labels.
 */
export const FINANCE_HQ_SALES_SHEET_COLUMNS: ExcelSheetColumn[] = [
  { id: "orderNumber", label: "ORDER ID", type: "text", editable: false, minWidth: 110, group: "ORDER" },
  { id: "productName", label: "PRODUCT", type: "text", editable: false, minWidth: 160, group: "ORDER" },
  { id: "mfgInvoiceNumber", label: "INV ID", type: "text", editable: true, minWidth: 140, group: "INVOICE" },
  { id: "mfgInvoiceDate", label: "INV DATE", type: "date", popupEdit: true, minWidth: 110, group: "INVOICE" },
  { id: "invoiceDueDate", label: "INV DUE DATE", type: "date", popupEdit: true, minWidth: 120, group: "INVOICE" },
  { id: "manufacturerName", label: "MFR", type: "text", editable: false, minWidth: 160, group: "INVOICE" },
  { id: "practiceName", label: "PRACTICE", type: "text", editable: false, minWidth: 150, group: "INVOICE" },
  { id: "invReferenceId", label: "INV REFERENCE ID", type: "text", editable: false, minWidth: 170, group: "INVOICE" },
  { id: "invoiceStatus", label: "STATUS", type: "text", editable: false, minWidth: 130, colorFrom: "invoiceStatus", group: "INVOICE" },
  { id: "productUnits", label: "PRODUCT UNITS", type: "number", editable: false, align: "right", total: true, minWidth: 130, group: "INVOICE" },
  { id: "cogsPerUnit", label: "COG P/U", type: "currency", editable: false, align: "right", minWidth: 110, group: "INVOICE", showCurrencySymbol: true },
  { id: "cogsSalesOrig", label: "COGs/Sales Orig", type: "currency", editable: false, align: "right", total: true, minWidth: 220, group: "INVOICE", showCurrencySymbol: true },
  { id: "totalV3Cost", label: "V3 Total Cost", type: "currency", editable: false, align: "right", total: true, minWidth: 250, group: "INVOICE", showCurrencySymbol: true },
  { id: "netInvoiceAmount", label: "NET INV AMT", type: "currency", editable: false, align: "right", total: true, minWidth: 130, group: "INVOICE", showCurrencySymbol: true },
  { id: "shippingCharges", label: "SHIPPING", type: "currency", popupEdit: true, align: "right", total: true, minWidth: 120, group: "INVOICE", showCurrencySymbol: true },
  { id: "lateFee", label: "LATE FEE", type: "currency", popupEdit: true, align: "right", total: true, minWidth: 100, group: "INVOICE", showCurrencySymbol: true },
  { id: "invoiceTotal", label: "INVOICE TOTAL", type: "currency", editable: false, align: "right", total: true, minWidth: 140, colorFrom: "invoiceStatus", group: "INVOICE", showCurrencySymbol: true },
  { id: "paymentDue", label: "OUTSTANDING AMT", type: "currency", editable: false, align: "right", total: true, minWidth: 150, group: "INVOICE", showCurrencySymbol: true },
  { id: "paymentAmount", label: "PAYMENT AMT", type: "currency", popupEdit: true, popupEditIconWhenEmpty: true, dueField: "paymentDue", align: "right", total: true, minWidth: 130, subRowsFrom: FINANCE_HQ_SALES_SHEET_PAYMENT_SOURCE, colorFrom: "paymentStatus", group: "PAYMENT", showCurrencySymbol: true },
  { id: "netInvPmtAmt", label: "NET INV PMT AMT", type: "currency", editable: false, align: "right", total: true, minWidth: 140, subRowsFrom: FINANCE_HQ_SALES_SHEET_PAYMENT_SOURCE, group: "PAYMENT", showCurrencySymbol: true },
  { id: "paymentDate", label: "PAYMENT DATE", type: "date", popupEdit: true, popupEditIconWhenEmpty: true, minWidth: 120, subRowsFrom: FINANCE_HQ_SALES_SHEET_PAYMENT_SOURCE, group: "PAYMENT" },
  { id: "paymentStatus", label: "PMT STATUS", type: "text", editable: false, minWidth: 120, subRowsFrom: FINANCE_HQ_SALES_SHEET_PAYMENT_SOURCE, colorFrom: "paymentStatus", group: "PAYMENT" },
  { id: "v3CommPercent", label: "V3 %", type: "percent", editable: false, align: "right", minWidth: 90, subRowsFrom: FINANCE_HQ_SALES_SHEET_PAYMENT_SOURCE, rightClickTooltip: "V3 Service Rate", feeContextField: "_v3RateEdit", feeIcon: "edit", alwaysShowFeeIcon: true, group: "V3 SERVICE FEE" },
  { id: "v3NetComm", label: "V3 $", type: "currency", editable: false, align: "right", total: true, minWidth: 120, subRowsFrom: FINANCE_HQ_SALES_SHEET_PAYMENT_SOURCE, colorFrom: "v3CommStatus", rightClickTooltip: "V3 Revenue", feeContextField: "_v3Fee", secondaryAction: { tooltip: "Edit V3 Service Rate", contextField: "_v3RateEdit", icon: "edit" }, alwaysShowFeeIcon: true, group: "V3 SERVICE FEE", showCurrencySymbol: true },
  { id: "v3CommStatus", label: "V3 PAY STATUS", type: "text", editable: false, minWidth: 130, subRowsFrom: FINANCE_HQ_SALES_SHEET_PAYMENT_SOURCE, colorFrom: "v3CommStatus", group: "V3 SERVICE FEE" },
  { id: "v3ReceivedDate", label: "V3 RECEIVED DATE", type: "date", editable: false, minWidth: 140, subRowsFrom: FINANCE_HQ_SALES_SHEET_PAYMENT_SOURCE, group: "V3 SERVICE FEE" },
  { id: "agencyCommPercent", label: "AGENCY %", type: "percent", editable: false, align: "right", minWidth: 100, subRowsFrom: FINANCE_HQ_SALES_SHEET_PAYMENT_SOURCE, rightClickTooltip: "Agency Service Rate", feeContextField: "_agencyRateEdit", feeIcon: "edit", alwaysShowFeeIcon: true, group: "AGENCY SERVICE FEE" },
  { id: "agencyComm", label: "AGENCY $", type: "currency", editable: false, align: "right", total: true, minWidth: 120, subRowsFrom: FINANCE_HQ_SALES_SHEET_PAYMENT_SOURCE, colorFrom: "agencyCommStatus", rightClickTooltip: "Agency Service Fee", feeContextField: "_agencyFee", secondaryAction: { tooltip: "Edit Agency Service Fee Amount", contextField: "_agencyRateEdit", icon: "edit" }, alwaysShowFeeIcon: true, group: "AGENCY SERVICE FEE", showCurrencySymbol: true },
  { id: "agencyCommStatus", label: "AGENCY PAY STATUS", type: "text", editable: false, minWidth: 140, subRowsFrom: FINANCE_HQ_SALES_SHEET_PAYMENT_SOURCE, colorFrom: "agencyCommStatus", group: "AGENCY SERVICE FEE" },
  { id: "agencyPaidDate", label: "AGENCY PAID DATE", type: "date", editable: false, minWidth: 140, subRowsFrom: FINANCE_HQ_SALES_SHEET_PAYMENT_SOURCE, group: "AGENCY SERVICE FEE" },
  { id: "overrideCommPercent", label: "OVERRIDE %", type: "percent", editable: false, align: "right", minWidth: 110, subRowsFrom: FINANCE_HQ_SALES_SHEET_PAYMENT_SOURCE, nestedSubRowsFrom: FINANCE_HQ_SALES_SHEET_OVERRIDE_SOURCE, rightClickTooltip: "Override Service Rate", feeContextField: "_overrideRateEdit", feeIcon: "edit", alwaysShowFeeIcon: true, group: "OVERRIDE SERVICE FEE" },
  { id: "overrideComm", label: "OVERRIDE $", type: "currency", editable: false, align: "right", total: true, minWidth: 120, subRowsFrom: FINANCE_HQ_SALES_SHEET_PAYMENT_SOURCE, nestedSubRowsFrom: FINANCE_HQ_SALES_SHEET_OVERRIDE_SOURCE, colorFrom: "overrideCommStatus", rightClickTooltip: "Override Service Fee", feeContextField: "_overrideFee", secondaryAction: { tooltip: "Edit Override Service Fee Amount", contextField: "_overrideRateEdit", icon: "edit" }, alwaysShowFeeIcon: true, group: "OVERRIDE SERVICE FEE", showCurrencySymbol: true },
  { id: "overrideCommStatus", label: "OVERRIDE PAY STATUS", type: "text", editable: false, minWidth: 150, subRowsFrom: FINANCE_HQ_SALES_SHEET_PAYMENT_SOURCE, nestedSubRowsFrom: FINANCE_HQ_SALES_SHEET_OVERRIDE_SOURCE, colorFrom: "overrideCommStatus", group: "OVERRIDE SERVICE FEE" },
  { id: "overridePaidDate", label: "OVERRIDE PAID DATE", type: "date", editable: false, minWidth: 150, subRowsFrom: FINANCE_HQ_SALES_SHEET_PAYMENT_SOURCE, nestedSubRowsFrom: FINANCE_HQ_SALES_SHEET_OVERRIDE_SOURCE, group: "OVERRIDE SERVICE FEE" },
];

/** Applies CommissionType display label for V3 entity columns (grid UI only; export headers unchanged). */
export const applyV3EntityLabelsToFinanceHqColumns = (
  columns: ExcelSheetColumn[],
  v3EntityLabel: string = COMMISSION_TYPE_V3_DISPLAY_FALLBACK
): ExcelSheetColumn[] => {
  const upper = v3EntityLabel.toUpperCase();
  const commissionGroup = `${upper} SERVICE FEE`;

  return columns.map((col) => {
    switch (col.id) {
      case "totalV3Cost":
        return { ...col, label: `${v3EntityLabel} Total Cost` };
      case "v3CommPercent":
        return {
          ...col,
          label: `${upper} %`,
          group: commissionGroup,
          rightClickTooltip: `${v3EntityLabel} Service Rate`,
        };
      case "v3NetComm":
        return {
          ...col,
          label: `${upper} $`,
          group: commissionGroup,
          rightClickTooltip: "V3 Revenue",
          secondaryAction: col.secondaryAction
            ? {
                ...col.secondaryAction,
                tooltip: `Edit ${v3EntityLabel} Service Rate`,
              }
            : col.secondaryAction,
        };
      case "v3CommStatus":
        return { ...col, label: `${upper} PAY STATUS`, group: commissionGroup };
      case "v3ReceivedDate":
        return { ...col, label: `${upper} RECEIVED DATE`, group: commissionGroup };
      default:
        return col;
    }
  });
};

export const FINANCE_HQ_SALES_SHEET_SAVABLE_COLUMN_IDS: string[] = ["lateFee"];

export type FinanceHqExcelFetchOptions = {
  offset?: number;
  limit?: number;
  includeInvoiceSummary?: boolean;
  invoiceStatus?: string[];
  manufacturerIds?: number[];
  practiceIds?: number[];
  invoiceDateFrom?: string;
  invoiceDateTo?: string;
  mfgInvoiceNumber?: string;
  orderNumber?: string;
  patientIds?: number[];
  productIds?: number[];
  agencyIds?: number[];
  serialNumber?: string;
  usageStatus?: string;
  v3CommStatus?: string;
  agencyCommStatus?: string;
  ovrCommStatus?: string;
  v3ReceivedFromDate?: string;
  v3ReceivedToDate?: string;
  agencyPaidFromDate?: string;
  agencyPaidToDate?: string;
  ovrPaidFromDate?: string;
  ovrPaidToDate?: string;
  commPeriod?: string;
};

export const computeFinanceHqInvoiceTotal = (invoice: {
  invoiceAmount?: unknown;
  netInvoiceAmount?: unknown;
  shippingCharges?: unknown;
  lateFee?: unknown;
}): number => {
  const netAmount =
    invoice?.netInvoiceAmount !== null &&
    invoice?.netInvoiceAmount !== undefined &&
    invoice?.netInvoiceAmount !== ""
      ? parseExcelNumber(invoice.netInvoiceAmount)
      : parseExcelNumber(invoice?.invoiceAmount);
  return netAmount + parseExcelNumber(invoice?.shippingCharges) + parseExcelNumber(invoice?.lateFee);
};

const hasNumericValue = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== "";

const formatOptionalExcelCurrency = (value: unknown): string =>
  hasNumericValue(value) ? formatExcelCurrency(value) : "";

const formatOptionalExcelNumber = (value: unknown): string => {
  if (!hasNumericValue(value)) return "";
  const num = parseExcelNumber(value);
  return Number.isInteger(num) ? String(num) : String(num);
};

/** Export "COGs $ Total": cogsTotal, else cogsPerUnit * productUnits when both exist. */
export const computeFinanceHqCogsSalesOrig = (invoice: {
  cogsTotal?: unknown;
  cogsPerUnit?: unknown;
  productUnits?: unknown;
}): number | "" => {
  if (hasNumericValue(invoice?.cogsTotal)) return parseExcelNumber(invoice.cogsTotal);
  if (hasNumericValue(invoice?.cogsPerUnit) && hasNumericValue(invoice?.productUnits)) {
    return parseExcelNumber(invoice.cogsPerUnit) * parseExcelNumber(invoice.productUnits);
  }
  return "";
};

/** Primary Org total cost from Cost Breakdown: COGs + Shipping Charges (Actual). */
export const computeFinanceHqTotalV3Cost = (invoice: {
  v3TotalCost?: unknown;
  cogsAmount?: unknown;
  cogsTotal?: unknown;
  shippingChargesActual?: unknown;
}): number | "" => {
  if (hasNumericValue(invoice?.v3TotalCost)) return parseExcelNumber(invoice.v3TotalCost);
  const cogs = hasNumericValue(invoice?.cogsAmount) ? invoice.cogsAmount : invoice?.cogsTotal;
  const shipping = invoice?.shippingChargesActual;
  if (!hasNumericValue(cogs) && !hasNumericValue(shipping)) return "";
  return parseExcelNumber(cogs) + parseExcelNumber(shipping);
};

const commissionsOfType = (payment: any, agencyType: string): any[] =>
  (Array.isArray(payment?.commissions) ? payment.commissions : []).filter(
    (item: any) => String(item?.agencyType || "").toUpperCase() === agencyType
  );


  const mergedCommissionOfType = (payment: any, agencyType: string): any => {
  const matches = commissionsOfType(payment, agencyType);
  if (matches.length <= 1) return matches[0];
  const paidEntry = matches.find((c) => String(c?.commissionStatus || "").toUpperCase() === "PAID");
  const valuedEntry = matches.find(
    (c) =>
      parseExcelNumber(c?.expectedCommissionRate) !== 0 ||
      parseExcelNumber(c?.expectedCommissionAmount) !== 0 ||
      parseExcelNumber(c?.calculatedCommissionAmount) !== 0
  );
  const base = valuedEntry || matches[0];
  if (!paidEntry || paidEntry === base) return base;
  return {
    ...base,
    commissionStatus: paidEntry.commissionStatus,
    commissionReceivedDate: paidEntry.commissionReceivedDate ?? base.commissionReceivedDate,
  };
};

/** Accepts searchInvoiceList payments[] or a nested getPaymentsByInvoiceId payload. */
export const paymentsFromInvoice = (invoice: FinanceHqInvoiceRecord | any): any[] => {
  const payments = invoice?.payments;
  if (Array.isArray(payments)) return payments;
  if (payments && Array.isArray(payments.payments)) return payments.payments;
  if (Array.isArray(invoice?.paymentList)) return invoice.paymentList;
  return [];
};

export const sumPaymentCommissions = (payments: any[], agencyType?: string): number =>
  payments.reduce((sum, payment) => {
    const commissions = Array.isArray(payment?.commissions) ? payment.commissions : [];
    return (
      sum +
      commissions.reduce((inner: number, item: any) => {
        if (
          agencyType &&
          String(item?.agencyType || "").toUpperCase() !== agencyType.toUpperCase()
        ) {
          return inner;
        }
        return inner + parseExcelNumber(item?.expectedCommissionAmount);
      }, 0)
    );
  }, 0);

const financeHqExcelBaseSearch = (): Omit<
  SearchInvoiceInfo,
  "currentPage" | "itemsPerPage" | "isDownload"
> => ({
  fromDate: "",
  toDate: "",
  mfgIds: [],
  agencyIds: [],
  patientIds: [],
  practiceIds: [],
  productIds: [],
  mfgInvoiceNumber: "",
  serialNumber: "",
  orderNumber: "",
  invoiceStatus: [...FINANCE_HQ_EXCEL_INVOICE_STATUSES],
  usageStatus: "",
  excludePurchaseAndBill: true,
});

export const buildFinanceHqExcelSearchInfo = (
  options: FinanceHqExcelFetchOptions = {}
): SearchInvoiceInfo => {
  const offset = Math.max(0, options.offset ?? 0);
  const base = financeHqExcelBaseSearch();
  const limit = Math.max(
    1,
    options.limit ??
      (offset === 0 ? FINANCE_HQ_EXCEL_INITIAL_LIMIT_FALLBACK : FINANCE_HQ_EXCEL_LOAD_MORE_LIMIT_FALLBACK)
  );
  const includeInvoiceSummary = options.includeInvoiceSummary ?? offset === 0;

  return {
    ...base,
    currentPage: Math.floor(offset / limit) + 1,
    itemsPerPage: limit,
    isDownload: false,
    recordOffset: offset,
    recordLimit: limit,
    includePayments: true,
    includeProfitDetail: true,
    invoiceStatus: options.invoiceStatus?.length ? options.invoiceStatus : base.invoiceStatus,
    mfgIds: options.manufacturerIds?.length ? options.manufacturerIds : base.mfgIds,
    practiceIds: options.practiceIds?.length ? options.practiceIds : base.practiceIds,
    fromDate: options.invoiceDateFrom || base.fromDate,
    toDate: options.invoiceDateTo || base.toDate,
    mfgInvoiceNumber: options.mfgInvoiceNumber || base.mfgInvoiceNumber,
    orderNumber: options.orderNumber || base.orderNumber,
    patientIds: options.patientIds?.length ? options.patientIds : base.patientIds,
    productIds: options.productIds?.length ? options.productIds : undefined,
    agencyIds: options.agencyIds?.length ? options.agencyIds : base.agencyIds,
    serialNumber: options.serialNumber || base.serialNumber,
    usageStatus: options.usageStatus || base.usageStatus,
    v3CommStatus: options.v3CommStatus || undefined,
    agencyCommStatus: options.agencyCommStatus || undefined,
    ovrCommStatus: options.ovrCommStatus || undefined,
    v3ReceivedFromDate: options.v3ReceivedFromDate || undefined,
    v3ReceivedToDate: options.v3ReceivedToDate || undefined,
    agencyPaidFromDate: options.agencyPaidFromDate || undefined,
    agencyPaidToDate: options.agencyPaidToDate || undefined,
    ovrPaidFromDate: options.ovrPaidFromDate || undefined,
    ovrPaidToDate: options.ovrPaidToDate || undefined,
    commPeriod: options.commPeriod || undefined,
    ...(includeInvoiceSummary ? { includeInvoiceSummary: true } : {}),
  };
};

export type FinanceHqInvoiceRecord = invoiceInfo & {
  payments?: any[];
  cogsAmount?: number;
  shippingChargesActual?: number;
  v3TotalCost?: number;
};

export type FinanceHqFeeContext = {
  paymentId: number;
  invoiceId: number;
  agencyId: number;
  commissionAgencyType: "V3" | "AGENCY" | "OVERRIDE";
  expectedAmount: number;
  alreadyPaidAmount: number;
};

export type FinanceHqRateEditContext = {
  invoiceId: number;
  agencyId: number;
  paymentId: number;
  currentRate: number;
  agencyName: string;
  invoiceAmt: number;
};

export type FinanceHqOverrideSubRow = {
  overrideAgencyName: string;
  overrideCommPercent: string;
  overrideComm: string;
  overrideCommStatus: string;
  overridePaidDate: string;
  _overrideFee?: FinanceHqFeeContext;
  _overrideRateEdit?: FinanceHqRateEditContext;
};

export type FinanceHqPaymentSubRow = {
  paymentAmount: string;
  netInvPmtAmt: string;
  paymentDate: string;
  paymentStatus: string;
  v3CommPercent: string;
  v3NetComm: string;
  v3CommStatus: string;
  v3ReceivedDate: string;
  agencyPaidDate: string;
  agencyCommPercent: string;
  agencyComm: string;
  agencyCommStatus: string;
  overrides: FinanceHqOverrideSubRow[];
  _v3Fee?: FinanceHqFeeContext;
  _v3RateEdit?: FinanceHqRateEditContext;
  _agencyFee?: FinanceHqFeeContext;
  _agencyRateEdit?: FinanceHqRateEditContext;
};

const buildFeeContext = (
  payment: any,
  commission: any,
  agencyType: "V3" | "AGENCY" | "OVERRIDE"
): FinanceHqFeeContext | undefined => {
  const paymentId = Number(payment?.paymentId);
  const agencyId = Number(commission?.agencyId);
  const invoiceId = Number(commission?.invoiceId);
  if (!commission || !Number.isFinite(paymentId) || !Number.isFinite(agencyId) || !Number.isFinite(invoiceId)) {
    return undefined;
  }
  return {
    paymentId,
    invoiceId,
    agencyId,
    commissionAgencyType: agencyType,
    expectedAmount: parseExcelNumber(commission?.expectedCommissionAmount),
    alreadyPaidAmount: parseExcelNumber(commission?.calculatedCommissionAmount),
  };
};

const buildRateEditContext = (
  payment: any,
  commission: any,
  invoiceAmt: number
): FinanceHqRateEditContext | undefined => {
  const paymentId = Number(payment?.paymentId);
  const agencyId = Number(commission?.agencyId);
  const invoiceId = Number(commission?.invoiceId);
  if (!commission || !Number.isFinite(paymentId) || !Number.isFinite(agencyId) || !Number.isFinite(invoiceId)) {
    return undefined;
  }
  return {
    invoiceId,
    agencyId,
    paymentId,
    currentRate: parseExcelNumber(commission?.expectedCommissionRate),
    agencyName: String(commission?.agencyName || ""),
    invoiceAmt,
  };
};

const mapCommissionAmount = (
  commission: any,
  fallbackRate: unknown,
  netPaymentAmount: unknown
): { rate: unknown; amount: unknown } => {
  const rate = commission?.expectedCommissionRate ?? fallbackRate;
  const hasAmount =
    commission?.expectedCommissionAmount != null ||
    commission?.calculatedCommissionAmount != null ||
    (rate !== null && rate !== undefined && rate !== "");
  if (!hasAmount) return { rate: "", amount: "" };
 
  const calculatedAmount = commission?.calculatedCommissionAmount;
  const expectedIsZeroed =
    parseExcelNumber(commission?.expectedCommissionAmount) === 0 &&
    parseExcelNumber(rate) === 0 &&
    calculatedAmount != null &&
    parseExcelNumber(calculatedAmount) !== 0;
  if (expectedIsZeroed) {
    const net = parseExcelNumber(netPaymentAmount);
    const derivedRate = net !== 0 ? (parseExcelNumber(calculatedAmount) / net) * 100 : rate;
    return { rate: derivedRate, amount: calculatedAmount };
  }
  const amount =
    commission?.expectedCommissionAmount ??
    calculatedAmount ??
    (parseExcelNumber(netPaymentAmount) * parseExcelNumber(rate)) / 100;
  return { rate, amount };
};

export const mapInvoicePaymentsToSubRows = (
  invoice: FinanceHqInvoiceRecord
): FinanceHqPaymentSubRow[] => {
  const payments = paymentsFromInvoice(invoice);
  const invoiceAmt = computeFinanceHqInvoiceTotal(invoice);
  return payments.map((payment: any) => {
    const netPaymentAmount =
      payment?.adjustedAmount != null ? payment.adjustedAmount : payment?.paymentAmount;
    const v3 = mergedCommissionOfType(payment, "V3");
    const agencies = commissionsOfType(payment, "AGENCY");
    const overrides = commissionsOfType(payment, "OVERRIDE");
    const agency = agencies[0];
    const v3Mapped = mapCommissionAmount(v3, "", netPaymentAmount);
    const agencyMapped = mapCommissionAmount(
      agency,
      agencies.length === 0 ? invoice?.agencyCommissionRate : "",
      netPaymentAmount
    );
    const paymentDate = formatExcelDate(payment?.paymentDate);
    const overrideRows: FinanceHqOverrideSubRow[] = overrides.map((override: any, index: number) => {
      const overrideMapped = mapCommissionAmount(override, "", netPaymentAmount);
      const overrideCommStatus = String(override?.commissionStatus || "");
      return {
        overrideAgencyName:
          override?.agencyName || (index === 0 ? invoice?.overrideAgencyName : "") || "",
        overrideCommPercent: formatExcelPercent(overrideMapped.rate),
        overrideComm:
          overrideMapped.amount === "" ? "" : formatExcelCurrency(overrideMapped.amount),
        overrideCommStatus,
        overridePaidDate:
          overrideCommStatus === "PAID"
            ? formatExcelDate(override?.commissionReceivedDate) || paymentDate
            : "",
        _overrideFee: buildFeeContext(payment, override, "OVERRIDE"),
        _overrideRateEdit: buildRateEditContext(payment, override, invoiceAmt),
      };
    });
    if (overrideRows.length === 0) {
      const overrideMapped = mapCommissionAmount(
        undefined,
        invoice?.ovrAgencyCommissionRate,
        netPaymentAmount
      );
      overrideRows.push({
        overrideAgencyName: invoice?.overrideAgencyName || "",
        overrideCommPercent: formatExcelPercent(overrideMapped.rate),
        overrideComm:
          overrideMapped.amount === "" ? "" : formatExcelCurrency(overrideMapped.amount),
        overrideCommStatus: "",
        overridePaidDate: "",
      });
    }
    return {
      paymentAmount: formatExcelCurrency(payment?.paymentAmount),
      netInvPmtAmt: formatExcelCurrency(netPaymentAmount),
      paymentDate,
      paymentStatus: String(payment?.paymentStatus || ""),
      v3CommPercent: formatExcelPercent(v3Mapped.rate),
      v3NetComm: v3Mapped.amount === "" ? "" : formatExcelCurrency(v3Mapped.amount),
      v3CommStatus: String(v3?.commissionStatus || ""),
      v3ReceivedDate:
        v3?.commissionStatus === "PAID"
          ? formatExcelDate(v3?.commissionReceivedDate) || paymentDate
          : "",
      agencyCommPercent: formatExcelPercent(agencyMapped.rate),
      agencyComm: agencyMapped.amount === "" ? "" : formatExcelCurrency(agencyMapped.amount),
      agencyCommStatus: String(agency?.commissionStatus || ""),
      agencyPaidDate:
        agency?.commissionStatus === "PAID"
          ? formatExcelDate(agency?.commissionReceivedDate) || paymentDate
          : "",
      overrides: overrideRows,
      _v3Fee: buildFeeContext(payment, v3, "V3"),
      _v3RateEdit: buildRateEditContext(payment, v3, invoiceAmt),
      _agencyFee: buildFeeContext(payment, agency, "AGENCY"),
      _agencyRateEdit: buildRateEditContext(payment, agency, invoiceAmt),
    };
  });
};

export const mapInvoiceToFinanceHqSheetRow = (
  invoice: FinanceHqInvoiceRecord
): ExcelSheetRow => {
  const manufacturerName =
    invoice?.manufacturerName === "Migrate Manufacturer"
      ? "-"
      : invoice?.manufacturerName || "";
  const payments = paymentsFromInvoice(invoice);
  const netInvoiceAmount = invoice?.netInvoiceAmount ?? invoice?.invoiceAmount;
  const paymentSubRows = mapInvoicePaymentsToSubRows(invoice);
  const invoiceTotalRaw = computeFinanceHqInvoiceTotal(invoice);
  const totalPaidRaw = payments.reduce(
    (sum: number, payment: any) => sum + parseExcelNumber(payment?.paymentAmount),
    0
  );
  const backendRemaining = invoice?.remainingAmount ?? invoice?.outstandingAmount;
  const paymentDue =
    backendRemaining !== undefined && backendRemaining !== null && backendRemaining !== ""
      ? parseExcelNumber(backendRemaining)
      : invoiceTotalRaw - totalPaidRaw;

  return {
    invoiceId: invoice?.invoiceId ?? "",
    orderNumber: invoice?.orderNumber || "",
    productName: invoice?.productName !== "Migrate Product" ? invoice?.productName || "" : "-",
    mfgInvoiceNumber: invoice?.mfgInvoiceNumber || "",
    mfgInvoiceDate: formatExcelDate(invoice?.mfgInvoiceDate),
    invoiceDueDate: formatExcelDate(invoice?.invoiceDueDate),
    manufacturerName,
    practiceName: invoice?.practiceName || "",
    invReferenceId: invoice?.invReferenceId || "",
    invoiceStatus: invoice?.invoiceStatus || "",
    productUnits: formatOptionalExcelNumber(invoice?.productUnits),
    cogsSalesOrig: (() => {
      const cogsSalesOrig = computeFinanceHqCogsSalesOrig(invoice);
      return cogsSalesOrig === "" ? "" : formatExcelCurrency(cogsSalesOrig);
    })(),
    cogsPerUnit: formatOptionalExcelCurrency(invoice?.cogsPerUnit),
    totalV3Cost: (() => {
      const totalV3Cost = computeFinanceHqTotalV3Cost(invoice);
      return totalV3Cost === "" ? "" : formatExcelCurrency(totalV3Cost);
    })(),
    netInvoiceAmount: formatExcelCurrency(netInvoiceAmount),
    shippingCharges: formatExcelCurrency(invoice?.shippingCharges),
    lateFee: formatExcelCurrency(invoice?.lateFee),
    invoiceTotal: formatExcelCurrency(computeFinanceHqInvoiceTotal(invoice)),
    payments: paymentSubRows,
    paymentDue,
    orderId: invoice?.orderId ?? "",
    raworderNumber: invoice?.raworderNumber ?? invoice?.orderNumber ?? "",
    agencyName: invoice?.agencyName || "",
    stripeConfig: invoice?.stripeConfig,
    actualRemainingAmount: paymentDue,
    hasPaymentInstruction: !!invoice?.hasPaymentInstruction,
    paymentInstructionType: invoice?.paymentInstructionType || "",
    paymentInstructionValue: invoice?.paymentInstructionValue || "",
    creditAmount: invoice?.creditAmount ?? "",
  };
};

export type FinanceHqExcelPage = {
  rows: ExcelSheetRow[];
  totalElements: number;
  summary?: Record<string, number>;
};

const numberOrUndefined = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  const num = Number(value);
  return Number.isFinite(num) ? num : undefined;
};

export const normalizeFinanceHqExcelSummary = (
  summary?: Record<string, number>
): Record<string, number> | undefined => {
  if (!summary) return undefined;
  const invoiceAmount = numberOrUndefined(
    summary.invoiceAmount ?? (summary as any).totalInvoiceAmount
  );
  const netInvoiceAmount = numberOrUndefined(
    summary.netInvoiceAmount ?? (summary as any).totalNetInvoiceAmount
  );
  const shippingCharges = numberOrUndefined(
    summary.shippingCharges ?? (summary as any).totalShippingCharges
  );
  const lateFee = numberOrUndefined(summary.lateFee ?? (summary as any).totalLateFee);
  const netForTotal = netInvoiceAmount ?? invoiceAmount;
  const invoiceTotal =
    numberOrUndefined(summary.invoiceTotal ?? (summary as any).totalInvoiceTotal) ??
    (netForTotal != null || shippingCharges != null || lateFee != null
      ? (netForTotal || 0) + (shippingCharges || 0) + (lateFee || 0)
      : undefined);
  const paymentAmount = numberOrUndefined(
    summary.paymentAmount ?? (summary as any).totalPaymentAmount
  );
  const netInvPmtAmt = numberOrUndefined(
    summary.netInvPmtAmt ?? (summary as any).totalNetInvPmtAmt ?? (summary as any).totalAdjustedAmount
  );
  const v3NetComm = numberOrUndefined(summary.v3NetComm ?? (summary as any).totalV3NetComm);
  const agencyComm = numberOrUndefined(summary.agencyComm ?? (summary as any).totalAgencyComm);
  const overrideComm = numberOrUndefined(
    summary.overrideComm ?? (summary as any).totalOverrideComm
  );
  const productUnits = numberOrUndefined(
    summary.productUnits ?? (summary as any).totalProductUnits
  );
  const cogsSalesOrig = numberOrUndefined(
    summary.cogsSalesOrig ??
      summary.cogsTotal ??
      (summary as any).totalCogsSalesOrig ??
      (summary as any).totalCogsTotal
  );
  const totalV3Cost = numberOrUndefined(
    summary.totalV3Cost ?? (summary as any).totalV3TotalCost
  );
  const mapped: Record<string, number> = {};
  if (productUnits != null) mapped.productUnits = productUnits;
  if (cogsSalesOrig != null) mapped.cogsSalesOrig = cogsSalesOrig;
  if (totalV3Cost != null) mapped.totalV3Cost = totalV3Cost;
  if (netInvoiceAmount != null) mapped.netInvoiceAmount = netInvoiceAmount;
  if (shippingCharges != null) mapped.shippingCharges = shippingCharges;
  if (lateFee != null) mapped.lateFee = lateFee;
  if (invoiceTotal != null) mapped.invoiceTotal = invoiceTotal;
  if (paymentAmount != null) mapped.paymentAmount = paymentAmount;
  if (netInvPmtAmt != null) mapped.netInvPmtAmt = netInvPmtAmt;
  if (v3NetComm != null) mapped.v3NetComm = v3NetComm;
  if (agencyComm != null) mapped.agencyComm = agencyComm;
  if (overrideComm != null) mapped.overrideComm = overrideComm;
  return Object.keys(mapped).length ? mapped : undefined;
};

export const fetchFinanceHqExcelInvoices = async (
  options: FinanceHqExcelFetchOptions = {}
): Promise<FinanceHqExcelPage> => {
  const response = await getInvoiceList(buildFinanceHqExcelSearchInfo(options), true);
  const page = extractInvoiceListPage(response);
  return {
    rows: page.records.map(mapInvoiceToFinanceHqSheetRow),
    totalElements: page.totalElements,
    summary: normalizeFinanceHqExcelSummary(page.summary),
  };
};
